/**
 * ウォッチリスト機能のカスタムフック
 */

import { LegacyPortalEventService } from "@spwn-portal/grpc";
import { push } from "connected-react-router";
import { getEventGoodsStatus } from "features/eventGoods/types/EventGoodsStatus";
import { cartActions } from "modules/cart";
import { modalActions } from "modules/modal";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";
import { useConnectClient } from "utility/connectWeb";

import type { WatchlistItem } from "./types/WatchlistItem";
import type { LegacyPortalEventGoods } from "@spwn-portal/grpc";
import type { ReqAddToCartProduct } from "modules/cart";
import type { Store } from "store";

const convertToWatchlistItem = (
  resGoods: LegacyPortalEventGoods
): WatchlistItem => {
  return {
    eventGoodsId: resGoods.eventGoodsId,
    eventGoodsFirestoreId: resGoods.eventGoodsFirestoreId,
    eventId: resGoods.eventId,
    eventFirestoreId: resGoods.eventFirestoreId,
    className: resGoods.className,
    subClassName: resGoods.subClassName,
    thumbnail: resGoods.thumbnail,
    hasVariants: resGoods.hasVariants,
    price: resGoods.price ?? 0,
    releaseDateTime: Number(resGoods.releaseDateTime),
    closeDateTime: Number(resGoods.closeDateTime),
    hashtags: resGoods.hashtags,
    isInWatchlist: resGoods.isInWatchlist,
    eventGoodsStatus: getEventGoodsStatus(
      resGoods.salesStatus,
      resGoods.inventoryStatus
    ),
  };
};

const getOptimisticWatchlist = (
  goodsWatchlist: WatchlistItem[] | undefined,
  eventGoodsFirestoreId: string,
  isInWatchlist: boolean
) => {
  return (
    goodsWatchlist?.map((watchlistItem) => {
      if (watchlistItem.eventGoodsFirestoreId === eventGoodsFirestoreId) {
        return {
          ...watchlistItem,
          isInWatchlist,
        };
      }
      return watchlistItem;
    }) ?? []
  );
};

export const useUserWatchlist = () => {
  const dispatch = useDispatch();
  const client = useConnectClient(LegacyPortalEventService);
  const userId = useSelector((state: Store) => state.auth.user.uid);
  const isEmailVerified = useSelector(
    (state: Store) => state.auth.user.emailVerified
  );

  const watchlistKey =
    userId && isEmailVerified ? ["user_watchlist", userId] : null;
  // SWRを使用してウォッチリストデータを取得
  const { data: userWatchlist, mutate: mutateUserWatchlist } = useSWR(
    watchlistKey,
    async () => {
      const response = await client.getWatchlistEventGoods({});

      // レスポンスからFirestore IDを抽出
      return response.goods.map<WatchlistItem>(convertToWatchlistItem);
    },
    {
      onError: (err) => {
        console.error(err);
        dispatch(
          modalActions.toggleError({
            msg: "ウォッチリストの取得に失敗しました",
          })
        );
      },
    }
  );

  const removeFromWatchlist = useCallback(
    async (eventGoodsFirestoreId: string) => {
      try {
        const optimisticWatchlist = getOptimisticWatchlist(
          userWatchlist,
          eventGoodsFirestoreId,
          false
        );
        await mutateUserWatchlist(
          async () => {
            // API呼び出しでウォッチリストから削除
            await client.removeEventGoodsFromWatchlist({
              eventGoodsFirestoreId,
            });
            return optimisticWatchlist;
          },
          {
            revalidate: true,
            populateCache: true,
            optimisticData: optimisticWatchlist,
            rollbackOnError: true, // エラー時は楽観的更新を元に戻す
          }
        );
      } catch (err: unknown) {
        console.error(err);
        dispatch(
          modalActions.toggleError({
            msg: "ウォッチリストからの削除に失敗しました",
          })
        );
      }
    },
    [dispatch, client, mutateUserWatchlist, userWatchlist]
  );
  const addToCart = useCallback(
    (watchListItem: WatchlistItem) => {
      const addCartProduct: ReqAddToCartProduct[] = [
        {
          id: watchListItem.eventGoodsFirestoreId,
          productType: "goods" as const,
          count: 1,
          price: watchListItem.price,
          name: watchListItem.className,
          variantName: watchListItem.subClassName,
        },
      ];
      dispatch(
        cartActions.addToCart.started({
          body: {
            eventId: watchListItem.eventFirestoreId,
            products: addCartProduct,
          },
          actionOnModal: () =>
            dispatch(cartActions.toggleMyCart({ isOpen: true })),
        })
      );
    },
    [dispatch]
  );

  return { userWatchlist, removeFromWatchlist, addToCart };
};
export const useGoodsWatchlist = (eventGoodsFirestoreIds: string[]) => {
  const dispatch = useDispatch();
  const client = useConnectClient(LegacyPortalEventService);
  const userId = useSelector((state: Store) => state.auth.user.uid);
  const isEmailVerified = useSelector(
    (state: Store) => state.auth.user.emailVerified
  );
  const watchlistKey =
    userId && isEmailVerified && eventGoodsFirestoreIds.length > 0
      ? ["goods_watchlist", userId, eventGoodsFirestoreIds]
      : null;

  // SWRを使用してウォッチリストデータを取得
  const { data: goodsWatchlist, mutate: mutateGoodsWatchlist } = useSWR(
    watchlistKey,
    async () => {
      const response = await client.getAllEventGoodsByEventGoodsFirestoreIds({
        eventGoodsFirestoreIds,
      });

      // レスポンスからFirestore IDを抽出
      return response.goods.map<WatchlistItem>(convertToWatchlistItem);
    },
    {
      onError: (err) => {
        console.error(err);
      },
    }
  );

  // ウォッチリストに追加
  const addToWatchlist = useCallback(
    async (eventGoodsFirestoreId: string) => {
      try {
        const optimisticWatchlist = getOptimisticWatchlist(
          goodsWatchlist,
          eventGoodsFirestoreId,
          true
        );
        await mutateGoodsWatchlist(
          async () => {
            await client.addEventGoodsToWatchlist({
              eventGoodsFirestoreId,
            });
            return optimisticWatchlist; // APIがデータを返さないので一旦 optimisticData を返してrevalidateをtrueにして更新を行う
          },
          {
            revalidate: true,
            populateCache: true,
            optimisticData: optimisticWatchlist,
            rollbackOnError: true, // エラー時は楽観的更新を元に戻す
          }
        );
      } catch (err: unknown) {
        console.error(err);
        dispatch(
          modalActions.toggleError({
            msg: "ウォッチリストへの追加に失敗しました",
          })
        );
      }
    },
    [dispatch, client, mutateGoodsWatchlist, goodsWatchlist]
  );

  const removeFromWatchlist = useCallback(
    async (eventGoodsFirestoreId: string) => {
      try {
        // 楽観的更新用WatchlistItemList
        const optimisticWatchlist = getOptimisticWatchlist(
          goodsWatchlist,
          eventGoodsFirestoreId,
          false
        );

        await mutateGoodsWatchlist(
          async () => {
            // API呼び出しでウォッチリストから削除
            await client.removeEventGoodsFromWatchlist({
              eventGoodsFirestoreId,
            });
            return optimisticWatchlist;
          },
          {
            revalidate: true,
            populateCache: true,
            optimisticData: optimisticWatchlist,
            rollbackOnError: true, // エラー時は楽観的更新を元に戻す
          }
        );
      } catch (err: unknown) {
        console.error(err);
        dispatch(
          modalActions.toggleError({
            msg: "ウォッチリストからの削除に失敗しました",
          })
        );
      }
    },
    [dispatch, client, mutateGoodsWatchlist, goodsWatchlist]
  );

  const toggleWatchlist = useCallback(
    async (eventGoodsFirestoreId: string) => {
      if (!userId) {
        // 非ログインユーザーにはログインモーダルを表示
        dispatch(modalActions.toggleLoginMethod());
        return Promise.resolve();
      }
      // Email未認証の時のハンドリング
      if (!isEmailVerified) {
        dispatch(push("/signup/authentication"));
        return;
      }
      // 現在の状態をチェックして適切にトグル
      if (
        goodsWatchlist?.find(
          (watchlistItem) =>
            watchlistItem.eventGoodsFirestoreId === eventGoodsFirestoreId
        )?.isInWatchlist
      ) {
        await removeFromWatchlist(eventGoodsFirestoreId);
      } else {
        await addToWatchlist(eventGoodsFirestoreId);
      }
    },
    [
      goodsWatchlist,
      addToWatchlist,
      removeFromWatchlist,
      dispatch,
      userId,
      isEmailVerified,
    ]
  );
  return { goodsWatchlist, toggleWatchlist };
};
